import { useId, useRef } from 'react';
import NoticePanel from './NoticePanel';
import WhatsappMessagePreview from './WhatsappMessagePreview';
import { EMPTY_MEDIA_LABELS_FOR_SENT_CAMPAIGN } from '../constants/whatsapp';
import { useDialogFocus } from '../hooks/useDialogFocus';
import { useWhatsappTemplates } from '../hooks/useWhatsappTemplates';
import { campaignTitle } from '../utils/campaignTitle';
import type { Campaign, WhatsappTemplate } from '../types/whatsapp';

const NO_SHEET_VALUES = {};

const PLACEHOLDER_NOTE =
  'The numbered placeholders, like {{1}}, were filled in from the sheet for each person.';
const TODAY_VERSION_NOTE =
  'This is the template as it is on WhatsApp today. If it was edited after this comms went out, people received the older version.';

type TemplateMatch =
  | { kind: 'found'; template: WhatsappTemplate }
  | { kind: 'not-found' }
  | { kind: 'language-unclear' };

const NOT_FOUND: TemplateMatch = { kind: 'not-found' };
const LANGUAGE_UNCLEAR: TemplateMatch = { kind: 'language-unclear' };

interface CampaignMessageModalProps {
  campaign: Campaign | null;
  onClose: () => void;
}

function findCampaignTemplate(
  templates: WhatsappTemplate[],
  campaign: Campaign,
): TemplateMatch {
  const sameName = templates.filter(
    (template) => template.name === campaign.template_name,
  );

  if (campaign.template_language) {
    const sameLanguage = sameName.find(
      (template) => template.language === campaign.template_language,
    );
    return sameLanguage ? { kind: 'found', template: sameLanguage } : NOT_FOUND;
  }

  if (sameName.length === 0) return NOT_FOUND;
  if (sameName.length > 1) return LANGUAGE_UNCLEAR;
  return { kind: 'found', template: sameName[0] };
}

function SentMessagePreview({ template }: { template: WhatsappTemplate }) {
  return (
    <>
      <WhatsappMessagePreview
        template={template}
        values={NO_SHEET_VALUES}
        emptyMediaLabels={EMPTY_MEDIA_LABELS_FOR_SENT_CAMPAIGN}
      />
      <p className="text-xs text-gray-500">{PLACEHOLDER_NOTE}</p>
      <p className="text-xs text-gray-500">{TODAY_VERSION_NOTE}</p>
    </>
  );
}

function CampaignMessageBody({ campaign }: { campaign: Campaign }) {
  const templatesQuery = useWhatsappTemplates();

  if (templatesQuery.isPending) {
    return <p className="text-sm text-gray-500">Loading the message...</p>;
  }

  if (templatesQuery.isError) {
    return (
      <NoticePanel
        title="Could not load the message"
        actionLabel="Try again"
        onAction={() => templatesQuery.refetch()}
      >
        WhatsApp did not answer just now. Nothing is wrong with the comms.
      </NoticePanel>
    );
  }

  const match = findCampaignTemplate(templatesQuery.data, campaign);
  if (match.kind === 'found') {
    return <SentMessagePreview template={match.template} />;
  }
  if (match.kind === 'language-unclear') {
    return (
      <NoticePanel title="Cannot tell which language was used">
        This template exists in more than one language, and this comms does not
        say which one it used, so the message cannot be shown.
      </NoticePanel>
    );
  }

  return (
    <NoticePanel title="Template no longer available">
      This template is no longer approved on WhatsApp, so it cannot be shown.
    </NoticePanel>
  );
}

export default function CampaignMessageModal({
  campaign,
  onClose,
}: CampaignMessageModalProps) {
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  useDialogFocus(panelRef, { isOpen: campaign !== null, onClose });

  if (!campaign) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="relative flex max-h-[85vh] w-full max-w-md flex-col rounded-lg bg-white shadow-xl"
      >
        <div className="border-b border-gray-200 px-6 py-4">
          <h3 id={titleId} className="text-lg font-semibold text-gray-900">
            {campaignTitle(campaign)}
          </h3>
        </div>
        <div className="flex-1 space-y-3 overflow-y-auto px-6 py-4">
          <CampaignMessageBody campaign={campaign} />
        </div>
        <div className="flex justify-end border-t border-gray-200 px-6 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
